/**
 * Offline drainer for the `db.fieldScoutQueue` table.
 *
 * Each queued item holds a `payload` that is POSTed to
 * the single-photo and round routes (POST /api/v1/scouting/field-photo, then field-round).
 *
 * Status machine mirrors syncPendingSessions / phenologySyncService:
 *   pending  → syncing → synced  (success)
 *   pending  → syncing → failed  (error, retryCount < QUARANTINE_THRESHOLD)
 *   failed   → syncing → failed  (retry, retryCount still < threshold)
 *   failed   → syncing → quarantined (retryCount reaches QUARANTINE_THRESHOLD)
 *
 * Track: field_scout_recording_mobile / Slice 2 / Task S2-5.
 */
import axios from 'axios';
import { db, FieldScoutQueueItem } from '../db';
import { getSignedInTenantId } from './signedInTenant';
import { beginAttempt, endAttempt, isBackedOff } from './fieldScoutBackoff';

const QUARANTINE_THRESHOLD = 5;

let fieldScoutSyncInProgress = false;

/**
 * Exported for tests only — resets the in-progress lock between test cases.
 */
export function __resetFieldScoutSyncFlagForTesting(): void {
  fieldScoutSyncInProgress = false;
}

const API = process.env.REACT_APP_API_URL || '/api/v1';

/**
 * Upload one queued round: every photo as its own multipart request (strictly
 * one after another, so the phone holds a single photo body at a time), then
 * the round itself with the returned storage keys. The photo route is
 * idempotent on (uuid, inspectionPointId, index), so a retry re-sends to the
 * same keys. Returns the mediaIds that were uploaded so the caller can free
 * them once the round is confirmed.
 */
export async function uploadRound(item: FieldScoutQueueItem): Promise<{ mediaIds: number[] }> {
  const payload = item.payload;
  const manifest = Array.isArray(payload?.photos) ? payload.photos : [];
  const mediaIds: number[] = [];
  const photos: Array<{ inspectionPointId: number; s3Keys: string[] }> = [];

  for (const group of manifest) {
    const ids: number[] = Array.isArray(group.mediaIds) ? group.mediaIds : [];
    const s3Keys: string[] = [];
    let index = 0;
    for (const id of ids) {
      const media = await db.media.get(id);
      const data = media?.data as Blob | undefined;
      // A missing or 0-byte photo is skipped like a missing row; the round still sends.
      if (!data || data.size === 0) continue;
      const form = new FormData();
      form.append('uuid', item.uuid);
      form.append('inspectionPointId', String(group.inspectionPointId));
      form.append('index', String(index));
      if (item.tenantId) form.append('tenantId', item.tenantId);
      // References the stored bytes (no copy); only the type label is filled in when empty.
      const body = data.type ? data : new Blob([data], { type: media?.type || 'image/jpeg' });
      form.append('photo', body, `p${group.inspectionPointId}_${index}.jpg`);
      const res = await axios.post(`${API}/scouting/field-photo`, form, { withCredentials: true });
      const key = res?.data?.s3Key;
      if (typeof key !== 'string' || !key) throw new Error('field-photo answered without a storage key');
      s3Keys.push(key);
      mediaIds.push(id);
      index += 1;
    }
    if (s3Keys.length > 0) {
      photos.push({ inspectionPointId: group.inspectionPointId, s3Keys });
    }
  }

  await axios.post(
    `${API}/scouting/field-round`,
    { ...payload, tenantId: item.tenantId, photos },
    { withCredentials: true },
  );
  return { mediaIds };
}

/**
 * Resets items stuck in 'syncing' (e.g. from a previous crash/refresh) back
 * to 'pending', and resets 'quarantined' items back to 'pending' with a
 * cleared retryCount so they will be retried.
 *
 * Returns the total number of rows modified.
 */
export const resetFieldScoutSyncState = async (): Promise<number> => {
  let count = 0;

  count += await db.fieldScoutQueue
    .where('syncStatus')
    .equals('syncing')
    .modify({ syncStatus: 'pending' });

  count += await db.fieldScoutQueue
    .where('syncStatus')
    .equals('quarantined')
    .modify({ syncStatus: 'pending', retryCount: 0 });

  return count;
};

/**
 * Drain all pending / previously-failed field scout queue items by POSTing
 * each item's photos one at a time to /scouting/field-photo and then its
 * round to /scouting/field-round.
 *
 * Guards:
 *   - in-progress lock (one drain at a time per tab)
 *   - navigator.onLine check (no-op while offline)
 *   - farm guard: nothing is sent when no farm is signed in; rounds queued
 *     before the update are stamped with the signed-in farm; a round recorded
 *     on another farm is left waiting untouched
 *   - back-off: a round that keeps killing the page is skipped (see
 *     fieldScoutBackoff); an attempt marker brackets every upload
 */
export const syncPendingFieldScout = async (): Promise<void> => {
  if (fieldScoutSyncInProgress) {
    return;
  }

  if (!navigator.onLine) {
    return;
  }

  const tenant = getSignedInTenantId();
  if (tenant === null) {
    return;
  }

  fieldScoutSyncInProgress = true;

  try {
    // Rounds queued before the update carry no farm: they belong to whoever
    // is signed in on this phone.
    await db.fieldScoutQueue
      .where('syncStatus')
      .anyOf(['pending', 'failed'])
      .filter(r => !r.tenantId)
      .modify({ tenantId: tenant });

    const pending = await db.fieldScoutQueue
      .where('syncStatus')
      .anyOf(['pending', 'failed'])
      .toArray();

    if (pending.length === 0) {
      return;
    }

    console.log(`[FieldScoutSync] Draining ${pending.length} queued item(s)...`);

    for (const item of pending) {
      // Another farm's round, or one inside its back-off window: leave it
      // exactly as it is (no status change, no marker, no retry count).
      if (item.tenantId !== tenant) continue;
      if (isBackedOff(item.uuid)) continue;

      try {
        // Mark syncing before the network call to prevent other tabs / triggers
        // from picking up the same item concurrently.
        await db.fieldScoutQueue.update(item.id!, { syncStatus: 'syncing' });

        // Photos one at a time as files, then the round (see uploadRound).
        beginAttempt(item.uuid);
        const { mediaIds } = await uploadRound(item);
        endAttempt(item.uuid);

        await db.fieldScoutQueue.update(item.id!, {
          syncStatus: 'synced',
          syncedAt: new Date().toISOString(),
        });

        // Free local storage: the photos are now on the server, so delete the
        // local blobs (prevents unbounded IndexedDB growth on the device).
        if (mediaIds.length > 0) {
          await db.media.bulkDelete(mediaIds);
        }
        console.log(`[FieldScoutSync] Item ${item.id} (uuid: ${item.uuid}) synced.`);
      } catch (err) {
        endAttempt(item.uuid);

        if ((err as any)?.response?.status === 409) {
          // Recorded on a different farm: not a failure of the round, so no
          // retry is counted. It waits until that farm signs in.
          console.warn(
            `[FieldScoutSync] Item ${item.id} (uuid: ${item.uuid}) belongs to a different farm; left pending.`,
          );
          await db.fieldScoutQueue.update(item.id!, { syncStatus: 'pending' });
          continue;
        }

        const newRetryCount = (item.retryCount ?? 0) + 1;
        const newStatus =
          newRetryCount >= QUARANTINE_THRESHOLD ? 'quarantined' : 'failed';

        if (newStatus === 'quarantined') {
          console.warn(
            `[FieldScoutSync] Item ${item.id} quarantined after ${newRetryCount} failed attempts.`,
          );
        } else {
          console.warn(
            `[FieldScoutSync] Item ${item.id} failed (attempt ${newRetryCount}):`,
            err,
          );
        }

        await db.fieldScoutQueue.update(item.id!, {
          syncStatus: newStatus,
          retryCount: newRetryCount,
        });
      }
    }
  } finally {
    fieldScoutSyncInProgress = false;
  }
};

/**
 * How many scouting rounds on this phone have not been uploaded yet
 * (pending, syncing, failed or quarantined). Synced rounds do not count.
 */
export async function countUnsentRounds(): Promise<number> {
  return db.fieldScoutQueue
    .where('syncStatus')
    .anyOf(['pending', 'syncing', 'failed', 'quarantined'])
    .count();
}
